import React, { useContext } from 'react';

import { HeaderStyledNav, HeaderStyledNavWrapper } from './AudiHeaderNavStyles';
import AudiHeaderActiveBar from '../AudiHeaderActiveBar/AudiHeaderActiveBar';
import AudiHeaderNavItem from '../AudiHeaderNavItem/AudiHeaderNavItem';
import type { AudiHeaderNavProps } from '../../interfaces/header-components.interfaces';
import AudiHeaderDealerContext from '../AudiHeaderDealerContext/AudiHeaderDealerContext';
import { DealerContext } from '../../context';
import DealerSearchMobile from '../AudiHeaderDealerContext/DealerSearchMobile';
import { ServiceContext } from '../../context/ServiceContext';
import { getDealerSearchHashParam } from '../../utils/append-partner-id';
import { useI18n } from '@oneaudi/i18n-context';
import { messages } from '../../i18n/messages';
import { dealerSearchParams } from '../../utils/nav-utils';

const AudiHeaderNav: React.FC<AudiHeaderNavProps> = ({
  navigationWrapperElement,
  headerNavigationItemsAmount,
  navigation,
  headerDispatch,
  headerState,
  isClosing,
  isOpen,
  onNavigationLinkClick,
  useFootnoteReferenceServiceTextParserHook,
  useOneLayer,
  windowWidth,
  onClickDealerContext,
  onClickDealerSearchContext,
  showDealerAdditionalData,
  dealerSearch,
  dealerSearchGeolocationEnabled,
  isTierOneDomain,
}) => {
  const {
    dealerData,
    renderDealerContext,
    geoError,
    autoConnectedUnsubscribed,
    dealerConnectedNotification,
  } = useContext(DealerContext);
  const { headerStateService } = useContext(ServiceContext);
  const isSubNavOpen = headerState.activeItem.index !== -1;
  const mainNavAriaLabel = useI18n(messages.main_nav_aria_label);

  let isSmallView = true;
  headerStateService.registerCallback((headerStateRemote) => {
    isSmallView = headerStateRemote.isMobileView;
  });

  // has the market opted in for geolocation 'optIn', then show the DealerSearch FA
  const isDealerSearchAlphaOptIn =
    isSmallView &&
    dealerData &&
    !dealerSearchParams() &&
    !dealerConnectedNotification?.isConnected &&
    dealerConnectedNotification?.geoLocationOption === 'optIn';

  const showDealerContext =
    !!dealerData &&
    renderDealerContext &&
    isTierOneDomain &&
    isSmallView &&
    !isDealerSearchAlphaOptIn;

  const showDealerSearch =
    isSmallView && !dealerData && renderDealerContext && dealerSearch && isTierOneDomain;

  const gelocationEnabled =
    dealerSearchGeolocationEnabled && !autoConnectedUnsubscribed && !geoError;

  const fromDealerSearch =
    gelocationEnabled ||
    headerState.activeItem.showDealerSearch ||
    getDealerSearchHashParam('consumerLocations');

  return (
    <HeaderStyledNavWrapper
      ref={navigationWrapperElement}
      headerNavigationItemsAmount={headerNavigationItemsAmount}
      isSmallView={isSmallView}
      isOpen={isOpen}
      isSubNavOpen={isSubNavOpen}
      showSearch={headerState.activeItem.showSearch}
      showDealerSearch={headerState.activeItem.showDealerSearch}
    >
      <HeaderStyledNav
        id="main-navigation"
        aria-label={mainNavAriaLabel}
        headerNavigationItemsAmount={headerNavigationItemsAmount}
        isSmallView={isSmallView}
        isOpen={isOpen}
        isSubNavOpen={isSubNavOpen}
        showDealerSearch={headerState.activeItem.showDealerSearch}
        role="navigation"
      >
        {navigation?.map((navigationItem, index) => {
          const link = navigationItem?.Link;
          const key = link ? `${link?.Text}${link?.Url}` : `link-${index}`;

          return (
            <AudiHeaderNavItem
              headerDispatch={headerDispatch}
              headerNavigationItemsAmount={headerNavigationItemsAmount}
              headerState={headerState}
              index={index}
              isClosing={isClosing}
              isSubNavOpen={isSubNavOpen}
              item={navigationItem}
              key={key}
              onNavigationLinkClick={onNavigationLinkClick}
              useFootnoteReferenceServiceTextParserHook={useFootnoteReferenceServiceTextParserHook}
              useOneLayer={useOneLayer}
            />
          );
        })}
      </HeaderStyledNav>
      {/* Dealer Context Mobile View */}
      {showDealerContext && (
        <AudiHeaderDealerContext
          isMobileView={isSmallView}
          headerNavigationItemsAmount={headerNavigationItemsAmount}
          onClickDealerContext={onClickDealerContext}
          onClickDealerSearchContext={() => {}}
          showDealerSearch={fromDealerSearch}
          showDealerAdditionalData={showDealerAdditionalData}
        />
      )}
      {/*Dealer Search Mobile View for geolocation 'optIn' */}
      {isDealerSearchAlphaOptIn && (
        <DealerSearchMobile
          isMobileView
          headerState={headerState}
          headerNavigationItemsAmount={headerNavigationItemsAmount}
          onClickDealerSearchContext={onClickDealerSearchContext}
          showDealerSearch={true}
          dealerSearchGeolocationEnabled={false}
          openDealerDetails={headerState.activeItem.openDealerDetails}
        />
      )}
      {/* Dealer Search Mobile View */}
      {showDealerSearch && (
        <DealerSearchMobile
          isMobileView
          headerState={headerState}
          headerNavigationItemsAmount={headerNavigationItemsAmount}
          onClickDealerSearchContext={onClickDealerSearchContext}
          showDealerSearch={
            gelocationEnabled || headerState.activeItem.showDealerSearch || dealerSearch
          }
          dealerSearchGeolocationEnabled={gelocationEnabled}
          openDealerDetails={headerState.activeItem.openDealerDetails}
        />
      )}
      <AudiHeaderActiveBar
        headerNavigationItemsAmount={headerNavigationItemsAmount}
        headerState={headerState}
        windowWidth={windowWidth}
      />
    </HeaderStyledNavWrapper>
  );
};

export default AudiHeaderNav;
